import {
  Breadcrumb,
  Button,
  DownloadExcel,
  ErrorAlert,
  EyeIcon,
  EyeSlashIcon,
  Input,
  Modal,
  modalHandler,
  ModalOpenBtn,
  PageHero,
  success,
  Table,
  TableRowsPerPage,
  updateList,
  UserGroupIcon,
  useCallback,
  useCustomForm,
  useDelete,
  useFetchData,
  useMemo,
  useState,
  useTableColumns,
} from "@";
import { getUsers, saveUser } from "./utils/api";
import { excelHeaderCellsName, usersFilter } from "./utils/helpers";
import { TableActions, tableColumns } from "./components/TableOptions";
import { createSchema, defaultValues, editSchema } from "./utils/validation";

const Index = () => {
  // State variables
  const [search, setSearch] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const links = [{ name: "المنسقين", link: "" }];
  const [showPassword, setShowPassword] = useState(false);

  // Fetch data
  const { data, setData, isLoading, error, pg, fetchData, updatePg } =
    useFetchData({
      fun: getUsers,
    });

  // Filtered items based on search
  const filteredItems = useMemo(
    () => usersFilter(data, search),
    [data, search],
  );

  // Delete handler
  const { deleteHandler } = useDelete(data, setData, "affiliateUsers");

  // Form handling
  const { reset, errors, register, handleSubmit, isSubmitting } = useCustomForm(
    defaultValues,
    isEdit ? editSchema : createSchema,
  );

  // Table columns
  const columns = useMemo(
    () =>
      useTableColumns(
        tableColumns,
        (row) => (
          <TableActions
            row={row}
            reset={reset}
            setIsEdit={setIsEdit}
            deleteHandler={deleteHandler}
          />
        ),
        1,
      ),
    [reset, setIsEdit, deleteHandler],
  );

  // Clear form
  const clearForm = useCallback(() => {
    setIsEdit(false);
    reset(defaultValues);
  }, [reset]);

  // Save data
  const saveData = useCallback(
    async (from) => {
      try {
        const { data: res } = await saveUser(from);
        setData(updateList(data, isEdit ? from : res.data));
        await success("تم التعديل بنجاح");
        modalHandler();
        clearForm();
      } catch (error) {}
    },
    [data, isEdit, clearForm, setData],
  );

  return (
    <>
      {/* Error alert */}
      <ErrorAlert reload={fetchData} show={error} />
      <Breadcrumb links={links} />

      {/* Modal for adding/editing user */}
      <Modal
        size="max-w-4xl"
        close={clearForm}
        title={isEdit ? "تعديل" : "اضافة"}
      >
        <form
          className="grid gap-5 p-2 md:grid-cols-2 md:p-5"
          onSubmit={handleSubmit(saveData)}
        >
          {/* Name */}
          <Input label="الاسم" error={errors.name} input={register("name")} />

          {/* Phone */}
          <Input
            label="رقم الجوال"
            error={errors.phone}
            input={register("phone")}
          />

          {/* Email */}
          <Input
            label="البريد الالكتروني"
            error={errors.email}
            input={register("email")}
            type="email"
          />

          {/* Password */}
          <div className="relative">
            <Input
              dir="ltr"
              star={isEdit}
              label="كلمة المرور"
              error={errors.password}
              autocomplete="new-password"
              styles="text-start input"
              input={register("password")}
              type={showPassword ? "text" : "password"}
              placeholder={
                isEdit && "اترك الحقل فارغاً في حالة عدم تغيير كلمة المرور"
              }
            />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="icon-style absolute end-0 top-8 rounded-2xl rounded-s-none"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-6 w-6" />
              ) : (
                <EyeIcon className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Submit */}
          <Button isLoading={isSubmitting} type="submit" title="حفظ البيانات" />
        </form>
      </Modal>

      <main className="table-card relative">
        <PageHero
          eyebrow="التسويق / المستخدمين"
          title="المنسقين"
          count={data?.length}
          icon={UserGroupIcon}
          hideCount={isLoading}
        >
          {/* Search */}
          <Input
            type="search"
            value={search}
            placeholder="بحث عن منسق"
            style="single-search-input"
            change={(e) => setSearch(e.target.value)}
          />

          <TableRowsPerPage
            pagination={pg}
            onRowsPerPageChange={(size) => updatePg({ size: size })}
          />

          {/* Download excel */}
          <DownloadExcel
            pageSize=""
            length={data?.length}
            fetch={getUsers}
            headerCellsName={excelHeaderCellsName}
          />

          {/* Add */}
          <ModalOpenBtn style="btn-green" title="إضافة " />
        </PageHero>

        {/* Table */}
        <Table
          pagination={pg}
          columns={columns}
          data={filteredItems}
          isLoading={isLoading}
          pageChange={(page) => updatePg({ page: page })}
          perRowsChange={(size) => updatePg({ size: size })}
        />
      </main>
    </>
  );
};

export default Index;
